import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosConfig';

const AdminTrialManagement = ({ universities, users }) => {
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUniversity, setSelectedUniversity] = useState('');
  const [selectedAccountType, setSelectedAccountType] = useState('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingTrial, setEditingTrial] = useState(null);
  const [message, setMessage] = useState('');

  // Form states
  const [selectedUser, setSelectedUser] = useState('');
  const [trialType, setTrialType] = useState('full');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');

  // Edit form states
  const [editTrialType, setEditTrialType] = useState('');
  const [editEndDate, setEditEndDate] = useState('');

  useEffect(() => {
    fetchTrials();
    // Set default dates
    const today = new Date().toISOString().split('T')[0];
    setStartDate(today);

    const defaultEndDate = new Date();
    defaultEndDate.setDate(defaultEndDate.getDate() + 30);
    setEndDate(defaultEndDate.toISOString().split('T')[0]);
  }, []);

  const fetchTrials = async (universityId = null) => {
    try {
      setLoading(true);
      const params = universityId ? `?university_id=${universityId}` : '';
      const response = await axios.get(`/api/v1/trial${params}`);
      setTrials(response.data.trials || []);
    } catch (error) {
      console.error('Error fetching trials:', error);
      setMessage('Error fetching trials');
    } finally {
      setLoading(false);
    }
  };

  const handleUniversityChange = (e) => {
    const universityId = e.target.value;
    setSelectedUniversity(universityId);
    fetchTrials(universityId || null);
  };

  const handleAccountTypeChange = (e) => {
    setSelectedAccountType(e.target.value);
  };

  const createTrial = async (e) => {
    e.preventDefault();
    setMessage('');

    try {
      const trialData = {
        trial: {
          user_id: parseInt(selectedUser),
          trial_start_date: startDate,
          trial_end_date: endDate,
          trial_type: trialType,
          notes: notes,
        },
      };

      await axios.post('/api/v1/trial/create', trialData);
      setMessage('Trial created successfully!');

      // Reset form
      setSelectedUser('');
      setNotes('');
      setShowCreateForm(false);

      // Refresh trials
      fetchTrials(selectedUniversity || null);
    } catch (error) {
      console.error('Error creating trial:', error);
      setMessage(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleEditTrial = (trial) => {
    setEditingTrial(trial);
    setEditTrialType(trial.trial_type);
    setEditEndDate(trial.trial_end_date);
    setShowEditForm(true);
  };

  const updateTrial = async (e) => {
    e.preventDefault();
    setMessage('');

    try {
      const updateData = {
        trial: {
          trial_type: editTrialType,
          trial_end_date: editEndDate,
        },
      };

      await axios.put(`/api/v1/trial/${editingTrial.trial_id}`, updateData);
      setMessage('Trial updated successfully!');

      // Reset form
      setShowEditForm(false);
      setEditingTrial(null);

      // Refresh trials
      fetchTrials(selectedUniversity || null);
    } catch (error) {
      console.error('Error updating trial:', error);
      setMessage(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  const deactivateTrial = async (trialId) => {
    const reason = window.prompt('Please provide a reason for deactivating this trial (optional):');

    if (reason === null) {
      return;
    }

    if (
      !window.confirm(
        'Are you sure you want to deactivate this trial? The user will see a message to contact support.'
      )
    ) {
      return;
    }

    try {
      await axios.post(`/api/v1/trial/${trialId}/deactivate`, { reason: reason || '' });
      setMessage('Trial deactivated successfully');
      fetchTrials(selectedUniversity || null);
    } catch (error) {
      console.error('Error deactivating trial:', error);
      setMessage(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  const revokeTrial = async (trialId) => {
    const reason = window.prompt('Please provide a reason for revoking this trial (optional):');

    if (reason === null) {
      // User cancelled
      return;
    }

    if (
      !window.confirm(
        "Are you sure you want to REVOKE this trial? This action is permanent and will immediately block the user's access."
      )
    ) {
      return;
    }

    try {
      await axios.post(`/api/v1/trial/${trialId}/revoke`, { reason: reason || '' });
      setMessage('Trial revoked successfully');
      fetchTrials(selectedUniversity || null);
    } catch (error) {
      console.error('Error revoking trial:', error);
      setMessage(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  const promoteTrial = async (trialId) => {
    const reason = window.prompt('Please provide a reason for promoting this user (optional):');

    if (reason === null) {
      // User cancelled
      return;
    }

    if (
      !window.confirm(
        'Are you sure you want to PROMOTE this user to a full personal account? This will give them permanent access to all features.'
      )
    ) {
      return;
    }

    try {
      await axios.post(`/api/v1/trial/${trialId}/promote`, {
        reason: reason || '',
        remove_trial_limits: true,
      });
      setMessage('User promoted to full account successfully!');
      fetchTrials(selectedUniversity || null);
    } catch (error) {
      console.error('Error promoting trial:', error);
      setMessage(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  const activateTrial = async (trialId) => {
    const reason = window.prompt('Please provide a reason for activating this trial (optional):');

    if (reason === null) {
      return;
    }

    try {
      await axios.post(`/api/v1/trial/${trialId}/activate`, { reason: reason || '' });
      setMessage('Trial activated successfully');
      fetchTrials(selectedUniversity || null);
    } catch (error) {
      console.error('Error activating trial:', error);
      setMessage(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  const unrevokeTrial = async (trialId) => {
    const reason = window.prompt('Please provide a reason for unrevoking this trial (optional):');

    if (reason === null) {
      return;
    }

    if (
      !window.confirm(
        "Are you sure you want to UNREVOKE this trial? This will restore the user's access."
      )
    ) {
      return;
    }

    try {
      await axios.post(`/api/v1/trial/${trialId}/unrevoke`, { reason: reason || '' });
      setMessage('Trial unrevoked successfully');
      fetchTrials(selectedUniversity || null);
    } catch (error) {
      console.error('Error unrevoking trial:', error);
      setMessage(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  const unpromoteTrial = async (trialId) => {
    const reason = window.prompt(
      'Please provide a reason for unpromoting (demoting) this user (optional):'
    );

    if (reason === null) {
      return;
    }

    if (
      !window.confirm(
        'Are you sure you want to UNPROMOTE (demote) this user back to trial status? They will lose full account access.'
      )
    ) {
      return;
    }

    try {
      await axios.post(`/api/v1/trial/${trialId}/unpromote`, { reason: reason || '' });
      setMessage('User demoted back to trial status successfully');
      fetchTrials(selectedUniversity || null);
    } catch (error) {
      console.error('Error unpromoting trial:', error);
      setMessage(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  const getTrialStatusBadge = (trial) => {
    // Check new status field first if available
    if (trial.status) {
      switch (trial.status) {
        case 'promoted':
          return (
            <span className="px-2 py-1 text-xs rounded-full bg-purple-200 text-purple-800">
              Promoted
            </span>
          );
        case 'revoked':
          return (
            <span className="px-2 py-1 text-xs rounded-full bg-red-200 text-red-800">Revoked</span>
          );
        case 'expired':
          return (
            <span className="px-2 py-1 text-xs rounded-full bg-orange-200 text-orange-800">
              Expired
            </span>
          );
        case 'active':
          return (
            <span className="px-2 py-1 text-xs rounded-full bg-green-200 text-green-800">
              Active
            </span>
          );
        case 'pending':
          return (
            <span className="px-2 py-1 text-xs rounded-full bg-blue-200 text-blue-800">
              Pending
            </span>
          );
        case 'inactive':
          return (
            <span className="px-2 py-1 text-xs rounded-full bg-gray-200 text-gray-600">
              Inactive
            </span>
          );
        default:
          break;
      }
    }

    // Fallback to old logic if status field not available
    if (trial.promoted) {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-purple-200 text-purple-800">
          Promoted
        </span>
      );
    }
    if (trial.revoked) {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-red-200 text-red-800">Revoked</span>
      );
    }
    if (!trial.is_active) {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-gray-200 text-gray-600">Inactive</span>
      );
    }
    if (trial.is_expired) {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-orange-200 text-orange-800">
          Expired
        </span>
      );
    }
    if (trial.is_current) {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-green-200 text-green-800">Active</span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs rounded-full bg-blue-200 text-blue-800">Scheduled</span>
    );
  };

  const getTrialTypeBadge = (type) => {
    const color =
      type === 'full' ? 'bg-purple-200 text-purple-800' : 'bg-orange-200 text-orange-800';
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${color}`}>
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </span>
    );
  };

  // Calculate trial metrics
  const getTrialMetrics = () => {
    const activeTrials = trials.filter(
      (t) => t.is_active && !t.is_expired && !t.revoked && !t.promoted
    );
    const expiredTrials = trials.filter((t) => t.is_expired && !t.revoked && !t.promoted);
    const revokedTrials = trials.filter((t) => t.revoked);
    const promotedTrials = trials.filter((t) => t.promoted);
    const trialUsers = users.filter((u) => u.account_type === 'trial');
    const fullTrials = activeTrials.filter((t) => t.trial_type === 'full');
    const basicTrials = activeTrials.filter((t) => t.trial_type === 'basic');
    const expiringSoon = activeTrials.filter((t) => t.days_remaining <= 7);

    return {
      totalTrialUsers: trialUsers.length,
      activeTrials: activeTrials.length,
      expiredTrials: expiredTrials.length,
      revokedTrials: revokedTrials.length,
      promotedTrials: promotedTrials.length,
      fullTrials: fullTrials.length,
      basicTrials: basicTrials.length,
      expiringSoon: expiringSoon.length,
    };
  };

  const metrics = getTrialMetrics();

  // Filter users based on both university and account type
  const filteredUsers = users.filter((user) => {
    // Filter by university if selected
    if (selectedUniversity && user.university_id !== parseInt(selectedUniversity)) {
      return false;
    }

    // Filter by account type
    if (selectedAccountType === 'trial') {
      return user.account_type === 'trial';
    } else if (selectedAccountType === 'university') {
      return user.account_type === 'university' || !user.account_type;
    }

    return true; // 'all' selected
  });

  return (
    <div className="bg-white border border-gray-200 shadow-md rounded-xl p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-blue-600">Trial Management</h2>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Create Trial
        </button>
      </div>

      {/* Trial Metrics Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-blue-600">{metrics.totalTrialUsers}</div>
          <div className="text-sm text-gray-600">Trial Users</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-green-600">{metrics.activeTrials}</div>
          <div className="text-sm text-gray-600">Active</div>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-purple-600">{metrics.fullTrials}</div>
          <div className="text-sm text-gray-600">Full</div>
        </div>
        <div className="bg-orange-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-orange-600">{metrics.basicTrials}</div>
          <div className="text-sm text-gray-600">Basic</div>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-yellow-600">{metrics.expiringSoon}</div>
          <div className="text-sm text-gray-600">Expiring</div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-gray-600">{metrics.expiredTrials}</div>
          <div className="text-sm text-gray-600">Expired</div>
        </div>
        <div className="bg-red-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-red-600">{metrics.revokedTrials}</div>
          <div className="text-sm text-gray-600">Revoked</div>
        </div>
        <div className="bg-indigo-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-indigo-600">{metrics.promotedTrials}</div>
          <div className="text-sm text-gray-600">Promoted</div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filter by Account Type:
          </label>
          <select
            value={selectedAccountType}
            onChange={handleAccountTypeChange}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Users</option>
            <option value="university">University Users</option>
            <option value="trial">Trial Users</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filter by University:
          </label>
          <select
            value={selectedUniversity}
            onChange={handleUniversityChange}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            disabled={selectedAccountType === 'trial'}
          >
            <option value="">All Universities</option>
            {universities.map((university) => (
              <option key={university.university_id} value={university.university_id}>
                {university.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div
          className={`mb-6 p-4 rounded-md ${message.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}
        >
          {message}
        </div>
      )}

      {/* Create Single Trial Form */}
      {showCreateForm && (
        <div className="mb-6 p-6 bg-gray-50 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4 text-blue-600">Create Single Trial</h3>
          <form onSubmit={createTrial} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User:</label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                required
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a user</option>
                {filteredUsers.map((user) => (
                  <option key={user.user_id} value={user.user_id}>
                    {user.first_name} {user.last_name} ({user.email})
                    {user.account_type === 'trial' && ' - TRIAL'}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trial Type:</label>
              <select
                value={trialType}
                onChange={(e) => setTrialType(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="full">Full (includes AI interviews)</option>
                <option value="basic">Basic (no AI interviews)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (optional):
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Optional notes about this trial..."
              />
            </div>

            <div className="md:col-span-2 flex space-x-3">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Create Trial
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit Trial Form */}
      {showEditForm && editingTrial && (
        <div className="mb-6 p-6 bg-yellow-50 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4 text-yellow-600">Edit Trial</h3>
          <form onSubmit={updateTrial} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User:</label>
              <input
                type="text"
                value={`${users.find((u) => u.user_id === editingTrial.user_id)?.first_name || ''} ${users.find((u) => u.user_id === editingTrial.user_id)?.last_name || ''}`}
                disabled
                className="w-full p-2 border border-gray-300 rounded-md bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trial Type:</label>
              <select
                value={editTrialType}
                onChange={(e) => setEditTrialType(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500"
              >
                <option value="full">Full (includes AI interviews)</option>
                <option value="basic">Basic (no AI interviews)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date:</label>
              <input
                type="date"
                value={editingTrial.trial_start_date}
                disabled
                className="w-full p-2 border border-gray-300 rounded-md bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date:</label>
              <input
                type="date"
                value={editEndDate}
                onChange={(e) => setEditEndDate(e.target.value)}
                required
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500"
              />
            </div>

            <div className="md:col-span-2 flex space-x-3">
              <button
                type="submit"
                className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
              >
                Update Trial
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowEditForm(false);
                  setEditingTrial(null);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Trials Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Dates
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Days Remaining
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Notes
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                  Loading trials...
                </td>
              </tr>
            ) : trials.length > 0 ? (
              trials.map((trial) => {
                const user = users.find((u) => u.user_id === trial.user_id);
                return (
                  <tr key={trial.trial_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        <div className="font-medium text-gray-900">
                          {user ? `${user.first_name} ${user.last_name}` : 'Unknown User'}
                          {user?.account_type === 'trial' && (
                            <span className="ml-2 px-2 py-1 text-xs rounded-full bg-blue-200 text-blue-800">
                              Trial
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">{user?.email || 'N/A'}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {getTrialTypeBadge(trial.trial_type)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      <div>{trial.trial_start_date}</div>
                      <div className="text-gray-500">to {trial.trial_end_date}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{getTrialStatusBadge(trial)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {trial.days_remaining > 0 ? `${trial.days_remaining} days` : '0 days'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">
                      {trial.notes || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <div className="flex flex-wrap gap-2">
                        {/* Show different actions based on trial status */}
                        {!trial.revoked && !trial.promoted && (
                          <>
                            {trial.is_active && !trial.is_expired && (
                              <button
                                onClick={() => handleEditTrial(trial)}
                                className="text-blue-600 hover:text-blue-800 font-medium"
                              >
                                Edit
                              </button>
                            )}

                            {trial.is_active ? (
                              <button
                                onClick={() => deactivateTrial(trial.trial_id)}
                                className="text-gray-600 hover:text-gray-800 font-medium"
                              >
                                Deactivate
                              </button>
                            ) : (
                              <button
                                onClick={() => activateTrial(trial.trial_id)}
                                className="text-green-600 hover:text-green-800 font-medium"
                              >
                                Activate
                              </button>
                            )}

                            <button
                              onClick={() => revokeTrial(trial.trial_id)}
                              className="text-red-600 hover:text-red-800 font-medium"
                            >
                              Revoke
                            </button>

                            <button
                              onClick={() => promoteTrial(trial.trial_id)}
                              className="text-green-600 hover:text-green-800 font-medium"
                            >
                              Promote
                            </button>
                          </>
                        )}

                        {trial.revoked && (
                          <>
                            <button
                              onClick={() => unrevokeTrial(trial.trial_id)}
                              className="text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Unrevoke
                            </button>
                            <span className="text-gray-500 italic">Access revoked</span>
                          </>
                        )}

                        {trial.promoted && (
                          <>
                            <button
                              onClick={() => unpromoteTrial(trial.trial_id)}
                              className="text-orange-600 hover:text-orange-800 font-medium"
                            >
                              Unpromote
                            </button>
                            <span className="text-green-600 italic">Full account</span>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                  No trials found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminTrialManagement;
